<template>
  <div class="container-fluid">
    <IntroTitle
      :title="$t('experience.booking.title')"
      padding="0px 20px"
      paddingDesktop="10px 50px"
      titleFontSize="1.2rem"
      titleMobileFontSize="1.3rem"
      descriptionAlign="center"
      descriptionMobileAlign="center"
      effect="top-to-bottom--effect"
    >
    </IntroTitle>
    <div class="booking">
      <div class="booking__info">
        <Suspense>
          <template #default>
            <BookingCard
              :title="translate(experience.title)"
              :image="imageHost + experience.reducedImageKey"
              :date="
                new Intl.DateTimeFormat('es-ES', {
                  year: 'numeric',
                  month: '2-digit',
                  day: 'numeric'
                }).format(slot)
              "
              :time="
                new Intl.DateTimeFormat('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit'
                }).format(slot)
              "
              :duration="duration"
              :audience="audience"
              @click="goTo(experience.experienceId)"
            >
            </BookingCard>
          </template>
          <template #fallback>
            <CardSkeleton />
          </template>
        </Suspense>
        <div class="pin-block">
          <div class="head-block">
            <div class="head-block__title">
              {{ esPinDePuerta ? $t('experience.booking.pin.title') : $t('experience.booking.code.title') }}
            </div>
            <div class="head-block__description">
              {{ esPinDePuerta ? $t('experience.booking.pin.text') : $t('experience.booking.code.text') }}
            </div>
          </div>
          <div class="pin-block__value">{{ appointment.turnCode }}</div>
          <div class="pin-block__aviso" v-if="pinPendiente">
            {{ $t('experience.booking.pin.pending') }}
          </div>
        </div>
      </div>
      <div v-if="showMap" class="booking__map">
        <CustomerMap :mapOptions="mapOptions" />
      </div>
      <div class="booking__action">
        <div
          class="button-movistar button-movistar__primary"
          @click="router.push({ path: '/' })"
        >
          {{ $t('experience.booking.return') }}
        </div>
      </div>
    </div>
    <modal :showModal="showModal" contentWidth="550px">
      <template #content>
        <div class="modal-success">
          <IconCircleCheck size="48" color="#019DF4" stroke-width="1" />
          <div class="modal-success__title">
            {{ $t('experience.success.title') }}
          </div>
          <div class="modal-success__subtitle">
            {{ $t('experience.success.subtitle') }}
          </div>
          <div class="modal-success__action">
            <div
              class="button-movistar button-movistar__primary"
              @click="showModal = false"
            >
              {{ $t('experience.success.continue') }}
            </div>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>
<script setup>
  import { ref, computed } from 'vue'
  import router from '@/router'
  import commonMethods from '@/commonMethods'

  import BookingCard from '@/components/movistar/cards/BookingCard'
  import CardSkeleton from '@/components/movistar/skeletons/CardSkeleton.vue'
  import IntroTitle from '@/components/movistar/common/IntroTitle.vue'
  import CustomerMap from '../components/movistar/common/CustomerMap.vue'
  import modal from '@/components/movistar/modals/Modal.vue'
  import { IconCircleCheck } from '@tabler/icons-vue'

  import { useStore } from 'vuex'
  import { onMounted } from 'vue'

  const store = useStore()
  const { translate } = commonMethods()

  const imageHost = ref(process.env.VUE_APP_IMAGE_HOST)

  const props = defineProps({
    audience: {
      type: String
    }
  })

  let showModal = ref(true)
  let showMap = ref(false)
  let experience = computed(() => store.getters['getExperience'])
  let slot = computed(() => store.getters['getSlot'])
  let experienceType = computed(() => store.state.experience.experienceType)
  let appointment = computed(() => store.getters['getAppointment'])
  const abreConPin = computed(() => Boolean(experience.value?.doorAccess))
  const esPinDePuerta = computed(
    () => abreConPin.value && /^\d{4,}#$/.test(String(appointment.value?.turnCode || ''))
  )
  const pinPendiente = computed(() => abreConPin.value && !esPinDePuerta.value)
  let mapOptions = ref({})

  let duration = ref(null)
  if (experience.value.attributes?.length > 0) {
    const durationAttribute = experience.value.attributes.find(
      (attribute) => attribute.experienceAttributeTypeCode === 'DURATION'
    )

    if (durationAttribute) {
      duration = durationAttribute.value
    }
  }

  onMounted(() => {
    checkExperience()
  })

  const checkExperience = () => {
    if (!experienceType.value) return

    const locationAttribute = experience.value.attributes?.find(
      (attribute) => attribute.experienceAttributeTypeCode === 'LOCATION'
    )

    mapOptions.value.wait = ''
    mapOptions.value.subtitle = translate(experienceType.value.title)
    mapOptions.value.floor = locationAttribute?.value
    mapOptions.value.image =
      experienceType.value.imageKey || `maps/${experienceType.value.code}.png`
    mapOptions.value.filter = mapOptions.value
    showMap.value = true
  }

  const goTo = (type) => {
    router.push({ name: type })
  }
</script>
<style lang="scss">
  @import '@/scss/mixings';

  .booking {
    @include d-flex(column, flex-start, center);
    gap: 3vh;
    height: 100%;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 15px;
    &::-webkit-scrollbar {
      display: none;
    }
    &::-webkit-scrollbar-horizontal {
      display: none;
    }
    &__info {
      @include d-flex(column, flex-start, center);
      gap: 25px;
      width: 100%;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }
      &::-webkit-scrollbar-horizontal {
        display: none;
      }
    }
    &__action {
      @media screen and (min-width: $sm-breakpoint) {
        display: none;
      }
    }
    @media screen and (min-width: $sm-breakpoint) {
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      gap: 20px;
      padding: 0px 50px 50px 50px;
      align-items: initial;
    }
    @media screen and (min-width: $lg-breakpoint) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  .pin-block__aviso {
    width: 100%;
    padding: 10px 12px;
    border-radius: 8px;
    background: #e6f5fd;
    color: var(--primary-10-color);
    font-size: var(--fs-small);
    line-height: var(--fs-x-large);
    text-align: center;
  }
  .pin-block {
    @include d-flex(column, center, center, 30px);
    padding: 20px;
    border: 1px #dddddd solid;
    border-radius: 8px;
    overflow: hidden;
    width: 100%;

    .head-block {
      @include d-flex(column, flex-start, initial, 10px);
      width: 100%;

      &__title {
        width: 100%;
        font-size: var(--fs-large);
        font-weight: var(--fw-bold);
        line-height: var(--fs-xx-large);
      }

      &__description {
        width: 100%;
        font-size: var(--fs-small);
        line-height: var(--fs-x-large);
      }
    }

    &__value {
      text-align: center;
      font-size: 2.5rem;
      line-height: 3.5rem;
      font-weight: var(--fw-bold);
    }
  }
  .modal-success {
    @include d-flex(column, flex-start, flex-start, 8px);
    line-height: 24px;

    &__title {
      font-weight: var(--fw-bold);
    }

    &__subtitle {
      font-size: var(--fs-small);
      word-wrap: break-word;
      color: var(--secondary-color);
    }

    &__action {
      padding: 16.11px 0px;
    }
  }
</style>
